// Merge a corrected material-line draft with preserved non-material BOM lines
// into a setManufacturingRecipeLines payload.

import { normalizeSlotLabel } from "@/lib/manufacturing/slotLabel";
import type { MfgRecipeDetailResponse, MfgRecipeLineRole } from "@/lib/manufacturing/types";
import type { DictatedRecipeDraft } from "@/lib/manufacturing/types/recipeDictation";

export type RecipeLineSetPayload = {
  material_id?: string;
  component_recipe_id?: string;
  material_use_id?: string;
  quantity: number;
  sort_order: number;
  role: MfgRecipeLineRole;
  swappable: boolean;
  upgrade_price_delta: number | null;
  slot_label: string;
};

/**
 * Build the full-replace line list for set-lines:
 * - corrected draft material lines (matched only; pending blocked by caller)
 * - original component + use lines preserved in order after materials
 */
export function buildCorrectedRecipeLinesPayload(
  draft: DictatedRecipeDraft,
  detail: MfgRecipeDetailResponse,
): { ok: true; lines: RecipeLineSetPayload[] } | { ok: false; error: string } {
  const materialLines: RecipeLineSetPayload[] = [];
  for (const line of draft.lines) {
    if (line.match.kind !== "matched") {
      return {
        ok: false,
        error: `Unmatched material "${line.material_name_heard}" — match or remove it before saving.`,
      };
    }
    const qty = Number(line.quantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      return {
        ok: false,
        error: `Invalid quantity on "${line.material_name_heard || line.slot_label}".`,
      };
    }
    materialLines.push({
      material_id: line.match.material_id,
      quantity: qty,
      sort_order: materialLines.length,
      role: line.role,
      swappable: line.role !== "structural",
      upgrade_price_delta: line.upgrade_price_delta,
      slot_label: normalizeSlotLabel(line.slot_label),
    });
  }

  const preserved: RecipeLineSetPayload[] = [];
  for (const l of detail.lines) {
    if (l.line_kind === "component" && l.component_recipe_id) {
      preserved.push({
        component_recipe_id: l.component_recipe_id,
        quantity: Number(l.quantity) || 1,
        sort_order: materialLines.length + preserved.length,
        role: l.role,
        swappable: l.swappable,
        upgrade_price_delta: l.upgrade_price_delta,
        slot_label: normalizeSlotLabel(l.slot_label || ""),
      });
      continue;
    }
    if (l.line_kind === "use" && l.material_use_id) {
      preserved.push({
        material_use_id: l.material_use_id,
        quantity: Number(l.quantity) || 1,
        sort_order: materialLines.length + preserved.length,
        role: l.role,
        swappable: l.swappable,
        upgrade_price_delta: l.upgrade_price_delta,
        slot_label: normalizeSlotLabel(l.slot_label || ""),
      });
    }
  }

  return { ok: true, lines: [...materialLines, ...preserved] };
}
